import { useState } from 'react';
import { Icon, Spinner } from './ui/Icon.js';
import { apiFetch, apiGet } from '../lib/api.js';
import { useToast } from '../hooks/useToast.js';
import { useBrand } from '../lib/brand-context.js';
import { ENGINE_LABELS } from '../types/index.js';
import type { CollectionRun } from '../types/index.js';

interface FailedJob {
  id: string;
  engine: string;
  status: string;
  error: string | null;
  // Spec 172 (#4): failure_class disambiguates a provider_disabled short-circuit
  // from a real auth_invalid_key / timeout / rate_limited failure.
  failure_class: string | null;
  prompt_snippet: string;
  brand_name: string;
  executed_at: string | null;
}

// Spec 172 (#4): friendly labels for the raw failure_class enum.
const FAILURE_CLASS_LABELS: Record<string, string> = {
  provider_disabled: 'provider disabled',
  auth_invalid_key: 'invalid API key',
  rate_limited: 'rate limited',
  timeout: 'timeout',
  upstream_error: 'upstream error',
  empty_response: 'empty response',
};

interface CollectionHistoryProps {
  history: CollectionRun[];
  loading: boolean;
  onRefresh?: () => void;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function CollectionHistory({ history, loading, onRefresh }: CollectionHistoryProps) {
  const { addToast } = useToast();
  const { activeTenantId } = useBrand();
  const [resuming, setResuming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancellingRun, setCancellingRun] = useState<string | null>(null);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [runDetails, setRunDetails] = useState<FailedJob[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const toggleRunDetails = async (triggeredAt: string) => {
    if (expandedRun === triggeredAt) {
      setExpandedRun(null);
      setRunDetails([]);
      return;
    }
    setExpandedRun(triggeredAt);
    setDetailsLoading(true);
    try {
      const tenantId = activeTenantId;
      if (!tenantId) return;
      const res = await apiGet<{ data: FailedJob[] }>(
        `/api/v1/tenants/${tenantId}/collection/run-details?triggered_at=${encodeURIComponent(triggeredAt)}`,
      );
      setRunDetails(res.data);
    } catch {
      setRunDetails([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  const totalPending = history.reduce((sum, r) => sum + (r.jobs_pending ?? 0), 0);

  const handleResume = async () => {
    setResuming(true);
    try {
      const res = await apiFetch('/api/v1/admin/scheduler/resume', { method: 'POST' });
      if (!res.ok) throw new Error('Resume failed');
      const result = await res.json() as { data: { processed: number; succeeded: number; failed: number } };
      addToast(`Resumed: ${result.data.succeeded} completed, ${result.data.failed} failed`, 'success');
      onRefresh?.();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Resume failed', 'error');
    } finally {
      setResuming(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await apiFetch('/api/v1/admin/scheduler/cancel', { method: 'POST' });
      if (!res.ok) throw new Error('Cancel failed');
      const result = await res.json() as { data: { cancelled: number } };
      addToast(`Cancelled ${result.data.cancelled} pending jobs`, 'success');
      onRefresh?.();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Cancel failed', 'error');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner className="text-violet-500" />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Collection history</h3>
        <p className="text-sm text-slate-500 text-center py-6">No collection runs recorded yet.</p>
      </div>
    );
  }

  return (
    <div className="p-5" data-testid="collection-history">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900">Collection history</h3>
        {totalPending > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleResume}
              disabled={resuming || cancelling}
              className="hub-btn hub-btn--ghost"
            >
              {resuming ? <Spinner  /> : <Icon name="bx-play" />}
              Resume ({totalPending} pending)
            </button>
            <button
              onClick={handleCancel}
              disabled={cancelling || resuming}
              className="flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              {cancelling ? <Spinner  /> : <Icon name="bx-square" />}
              Cancel
            </button>
          </div>
        )}
      </div>
      <div className="space-y-3">
        {history.map((run, i) => {
          const successRate = run.jobs_created > 0
            ? Math.round((run.jobs_completed / run.jobs_created) * 100)
            : 0;
          const hasFailed = run.jobs_failed > 0;
          const hasPending = (run.jobs_pending ?? 0) > 0;

          // Spec 172 (#5): terminal run status from the API (in_progress |
          // complete | partial | failed). Fall back to deriving it from counts
          // for older history rows that predate the field.
          const runStatus: 'in_progress' | 'complete' | 'partial' | 'failed' =
            run.run_status ??
            (hasPending || (run.jobs_running ?? 0) > 0
              ? 'in_progress'
              : run.jobs_completed === 0 && hasFailed
                ? 'failed'
                : hasFailed
                  ? 'partial'
                  : 'complete');

          let icon;
          if (runStatus === 'failed') {
            icon = <Icon name="bx-x-circle" className="flex-shrink-0 text-red-500" />;
          } else if (runStatus === 'partial') {
            icon = <Icon name="bx-x-circle" className="flex-shrink-0 text-amber-500" />;
          } else if (hasPending) {
            icon = <Icon name="bx-time-five" className="flex-shrink-0 text-amber-500" />;
          } else {
            icon = <Icon name="bx-check-circle" className="flex-shrink-0 text-green-500" />;
          }

          const isExpanded = expandedRun === run.triggered_at;
          const isExpandable = hasFailed;

          // Spec 095: three-way breakdown of pending + running. Older
          // history rows may not carry the new fields (the API surfaces
          // them additively). Fall back to single `jobs_pending` when
          // the breakdown is absent.
          const eligibleNow = run.jobs_eligible_now;
          const scheduledLater = run.jobs_scheduled_later;
          const runningNow = run.jobs_running ?? 0;
          const hasBreakdown = eligibleNow !== undefined && scheduledLater !== undefined;
          const isFullyTerminal = !hasPending && runningNow === 0;

          // Region dimension — per-region job split for multi-region runs.
          // Empty/absent for single-region runs, which render unchanged.
          const regionEntries = Object.entries(run.per_region ?? {}).sort(([a], [b]) =>
            a.localeCompare(b),
          );

          return (
            <div key={i}>
            <div
              className={`flex items-center gap-4 border-b border-[var(--hub-color-line)] px-4 py-3 ${isExpandable ? 'cursor-pointer hover:bg-slate-50' : ''}`}
              onClick={isExpandable ? () => toggleRunDetails(run.triggered_at) : undefined}
            >
              {icon}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-700">
                  {formatDate(run.triggered_at)}
                  {isExpandable && (
                    <span className="ml-1.5 inline-block align-middle">
                      {isExpanded ? <Icon name="bx-chevron-up" className="inline text-slate-400" /> : <Icon name="bx-chevron-down" className="inline text-slate-400" />}
                    </span>
                  )}
                </p>
                {isFullyTerminal ? (
                  <p className="text-xs text-slate-400">
                    {run.jobs_created} created · {run.jobs_completed} done
                    {hasFailed && <span className="text-red-400"> · {run.jobs_failed} failed</span>}
                    {/* Spec 172 (#5): an all-failed run is NOT "Complete". Badge the
                        real terminal status so 0-data runs read honestly. */}
                    {runStatus === 'failed' ? (
                      <span
                        className="ml-2 inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700"
                        data-testid="run-row-failed-pill"
                      >
                        ✕ Failed — no data
                      </span>
                    ) : runStatus === 'partial' ? (
                      <span
                        className="ml-2 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
                        data-testid="run-row-partial-pill"
                      >
                        ◐ Partial
                      </span>
                    ) : (
                      <span
                        className="ml-2 inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
                        data-testid="run-row-complete-pill"
                      >
                        ✓ Complete
                      </span>
                    )}
                  </p>
                ) : hasBreakdown ? (
                  <p
                    className="text-xs text-slate-400"
                    data-testid="run-row-breakdown"
                  >
                    {run.jobs_created} created · {run.jobs_completed} done
                    {(eligibleNow ?? 0) > 0 && (
                      <span className="text-amber-500"> · {eligibleNow} eligible now</span>
                    )}
                    {(scheduledLater ?? 0) > 0 && (
                      <span className="text-slate-500"> · {scheduledLater} scheduled later</span>
                    )}
                    {runningNow > 0 && (
                      <span className="text-teal-600"> · {runningNow} stuck</span>
                    )}
                    {hasFailed && <span className="text-red-400"> · {run.jobs_failed} failed</span>}
                  </p>
                ) : (
                  <p className="text-xs text-slate-400">
                    {run.jobs_created} created / {run.jobs_completed} completed
                    {hasFailed && <span className="text-red-400"> / {run.jobs_failed} failed</span>}
                    {hasPending && <span className="text-amber-500"> / {run.jobs_pending} pending</span>}
                  </p>
                )}
                {regionEntries.length > 0 && (
                  <p
                    className="mt-0.5 text-xs text-slate-400"
                    data-testid="collection-history-regions"
                  >
                    {regionEntries.map(([code, count], idx) => (
                      <span key={code}>
                        {idx > 0 && <span className="text-slate-300"> · </span>}
                        <span className="font-medium text-slate-500">{code}</span> {count}
                      </span>
                    ))}
                  </p>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-medium text-slate-700">{successRate}%</p>
                <p className="text-xs text-slate-400">
                  ${run.total_cost.toFixed(2)} / {formatDuration(run.duration_ms)}
                </p>
              </div>
              {hasPending && (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    setCancellingRun(run.triggered_at);
                    try {
                      const res = await apiFetch('/api/v1/admin/scheduler/cancel', {
                        method: 'POST',
                        body: JSON.stringify({ triggered_at: run.triggered_at }),
                      });
                      if (!res.ok) throw new Error('Cancel failed');
                      const result = await res.json() as { data: { cancelled: number } };
                      addToast(`Cancelled ${result.data.cancelled} pending jobs from this run`, 'success');
                      onRefresh?.();
                    } catch (err) {
                      addToast(err instanceof Error ? err.message : 'Cancel failed', 'error');
                    } finally {
                      setCancellingRun(null);
                    }
                  }}
                  disabled={cancellingRun === run.triggered_at}
                  className="flex-shrink-0 rounded-md p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                  title={`Cancel ${run.jobs_pending} pending jobs`}
                >
                  {cancellingRun === run.triggered_at ? <Spinner  /> : <Icon name="bx-x-circle" />}
                </button>
              )}
            </div>
            {isExpanded && (
              <div className="ml-10 mt-1 mb-2 hub-plate--flat px-4 py-3">
                {detailsLoading ? (
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Spinner  /> Loading details...
                  </div>
                ) : runDetails.length === 0 ? (
                  <p className="text-xs text-slate-400">No failed or cancelled jobs in this run.</p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-slate-600">{runDetails.length} failed/cancelled job{runDetails.length !== 1 ? 's' : ''}</p>
                    {runDetails.map((job) => (
                      <div key={job.id} className="flex items-start gap-3 text-xs">
                        <span className="flex-shrink-0 rounded-md bg-red-100 px-1.5 py-0.5 font-medium text-red-700">{ENGINE_LABELS[job.engine] ?? job.engine}</span>
                        {/* Spec 172 (#4): failure_class badge — tells a provider_disabled
                            short-circuit apart from a real auth/timeout/rate-limit failure. */}
                        {job.failure_class && (
                          <span
                            className="flex-shrink-0 rounded-md bg-slate-200 px-1.5 py-0.5 font-medium text-slate-600"
                            data-testid="run-detail-failure-class"
                            title={`failure_class: ${job.failure_class}`}
                          >
                            {FAILURE_CLASS_LABELS[job.failure_class] ?? job.failure_class}
                          </span>
                        )}
                        <span className="flex-shrink-0 text-slate-400">{job.brand_name}</span>
                        <span className="flex-1 min-w-0 truncate text-slate-600" title={job.prompt_snippet}>{job.prompt_snippet}</span>
                        <span className="flex-shrink-0 text-red-500 font-medium" title={job.error ?? ''}>{job.error ?? job.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
